/**
 * Dashboard tile-layout math (spec 038 Phase 2 — movable/resizable tiles).
 *
 * react-grid-layout works on a fixed column grid with absolute item boxes
 * `{ i, x, y, w, h }`. We seed the layout from CB's stored GridStack coords
 * (`widget.grid = { x, y, w, h }`) and let a per-user override (saved in
 * `preferencesStore`, keyed `{ [cellId]: {x,y,w,h} }`) take precedence.
 *
 * These are pure helpers so the drag/resize + keyboard logic is unit-testable
 * without mounting react-grid-layout (which needs real DOM measurement).
 */

export const GRID_COLS = 12
export const MIN_W = 2
export const MIN_H = 2
export const MAX_H = 12

const num = (v, fallback) => (Number.isFinite(Number(v)) ? Number(v) : fallback)

export const clampW = (w) => Math.min(Math.max(Math.round(num(w, 4)), MIN_W), GRID_COLS)
export const clampH = (h) => Math.min(Math.max(Math.round(num(h, 4)), MIN_H), MAX_H)
// x must leave room for the item's width on a GRID_COLS grid.
export const clampX = (x, w) => Math.min(Math.max(Math.round(num(x, 0)), 0), Math.max(0, GRID_COLS - clampW(w)))
export const clampY = (y) => Math.max(0, Math.round(num(y, 0)))

/**
 * Build the react-grid-layout array for a set of widgets, applying any saved
 * per-user override on top of the server coords. Falls back to a sensible
 * flow (index-based row) when coords are missing.
 */
export function toRglLayout(widgets = [], saved = null) {
  return (widgets || []).map((widget, idx) => {
    const g = widget.grid || {}
    const s = saved && saved[widget.cellId]
    const w = clampW(s?.w ?? g.w)
    const h = clampH(s?.h ?? g.h)
    const x = clampX(s?.x ?? g.x, w)
    // Prefer an explicit y (saved, then server); otherwise stack by index.
    const y = clampY(s?.y ?? (Number.isFinite(Number(g.y)) ? g.y : idx))
    return { i: String(widget.cellId), x, y, w, h }
  })
}

/** react-grid-layout array → the compact `{ [cellId]: {x,y,w,h} }` we persist. */
export function layoutToSaved(layout = []) {
  const out = {}
  for (const it of layout || []) {
    out[it.i] = { x: it.x, y: it.y, w: it.w, h: it.h }
  }
  return out
}

/** Keyboard move: nudge one item by (dx, dy) grid cells, clamped to the grid. */
export function moveItem(layout = [], id, dx = 0, dy = 0) {
  const key = String(id)
  return layout.map((it) =>
    it.i === key ? { ...it, x: clampX(it.x + dx, it.w), y: clampY(it.y + dy) } : it
  )
}

/** Keyboard resize: grow/shrink one item by (dw, dh), keeping it on the grid. */
export function resizeItem(layout = [], id, dw = 0, dh = 0) {
  const key = String(id)
  return layout.map((it) => {
    if (it.i !== key) return it
    const w = clampW(it.w + dw)
    return { ...it, w, x: clampX(it.x, w), h: clampH(it.h + dh) }
  })
}
